/**
 * WorkerJobCard.js - Job card component for the worker marketplace
 * Displays job info in a clean, scannable card format
 */
import React from 'react';
import { motion } from 'framer-motion';
import { 
  MapPin, Clock, DollarSign, Calendar, AlertCircle,
  Shirt, Truck, Car, Home, ShoppingBag, ChevronRight
} from 'lucide-react';

const serviceIcons = {
  cleaning: Home,
  laundry: Shirt,
  courier: Truck,
  rides: Car,
  errands: ShoppingBag,
  tasks: ShoppingBag
};

const serviceColors = {
  cleaning: 'bg-blue-500',
  laundry: 'bg-purple-500',
  courier: 'bg-orange-500',
  rides: 'bg-green-500',
  errands: 'bg-pink-500',
  tasks: 'bg-pink-500'
};

const WorkerJobCard = ({ job, onSelect, onPickup, compact = false }) => {
  const ServiceIcon = serviceIcons[job.service_type] || ShoppingBag;
  const serviceColor = serviceColors[job.service_type] || 'bg-gray-500';
  
  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return 'TBD';
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow';
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };
  
  // Format time for display
  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };
  
  const isUrgent = job.urgency_level === 'urgent' || job.is_urgent;
  const workerPay = job.worker_pay || job.display_pay || 0;
  const urgentBonus = job.urgent_fee_amount || 0;
  
  if (compact) {
    return (
      <motion.button
        onClick={() => onSelect?.(job)}
        className="w-full bg-[var(--background-paper)] border border-[var(--border-primary)] rounded-xl p-4 text-left hover:border-[var(--primary)] transition-all"
        whileTap={{ scale: 0.98 }}
        data-testid={`job-card-${job.id}`}
      >
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 ${serviceColor} rounded-lg flex items-center justify-center flex-shrink-0`}>
            <ServiceIcon className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="font-semibold text-[var(--text-primary)] truncate">
                {job.service_name || job.service_type?.toUpperCase()}
              </p>
              {isUrgent && (
                <span className="px-1.5 py-0.5 bg-red-500 text-white text-xs font-bold rounded">
                  URGENT
                </span>
              )}
            </div>
            <p className="text-sm text-[var(--text-muted)] truncate">
              {formatDate(job.scheduled_date)} • {formatTime(job.scheduled_time)} • {job.city || 'NC'}
            </p>
          </div>
          <div className="text-right flex-shrink-0">
            <p className="font-bold text-[var(--primary)]">${workerPay.toFixed(2)}</p>
            <ChevronRight className="w-4 h-4 text-[var(--text-muted)] ml-auto" />
          </div>
        </div>
      </motion.button>
    );
  }
  
  return (
    <motion.div
      className="bg-[var(--background-paper)] border border-[var(--border-primary)] rounded-xl overflow-hidden"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid={`job-card-${job.id}`}
    >
      {/* Header with service type and urgent badge */}
      <div className={`${serviceColor} px-4 py-3 flex items-center justify-between`}>
        <div className="flex items-center gap-3">
          <ServiceIcon className="w-6 h-6 text-white" />
          <div>
            <p className="font-bold text-white uppercase tracking-wide">
              {job.service_type?.replace('_', ' ')}
            </p>
            <p className="text-white/80 text-sm">{job.service_name}</p>
          </div>
        </div>
        {isUrgent && (
          <div className="flex items-center gap-1 bg-red-600 px-2 py-1 rounded-full">
            <AlertCircle className="w-3 h-3 text-white" />
            <span className="text-xs font-bold text-white">URGENT</span>
          </div>
        )}
      </div>
      
      {/* Job Details */}
      <div className="p-4 space-y-3">
        {/* Date & Time */}
        <div className="flex items-center gap-3">
          <Calendar className="w-5 h-5 text-[var(--text-muted)]" />
          <div>
            <p className="font-medium text-[var(--text-primary)]">
              {formatDate(job.scheduled_date)}
            </p>
            <p className="text-sm text-[var(--text-muted)]">
              {formatTime(job.scheduled_time)}
            </p>
          </div>
        </div>
        
        {/* Location */}
        <div className="flex items-center gap-3">
          <MapPin className="w-5 h-5 text-[var(--text-muted)]" />
          <div>
            <p className="font-medium text-[var(--text-primary)]">
              {job.city || 'Location TBD'}, {job.state || 'NC'}
            </p>
            {job.distance_miles && (
              <p className="text-sm text-[var(--text-muted)]">
                {job.distance_miles.toFixed(1)} miles away
              </p>
            )}
          </div>
        </div>
        
        {/* Duration */}
        <div className="flex items-center gap-3">
          <Clock className="w-5 h-5 text-[var(--text-muted)]" />
          <p className="text-[var(--text-primary)]">
            Est. {job.duration_hours ? `${job.duration_hours} hr${job.duration_hours > 1 ? 's' : ''}` : 'TBD'}
          </p>
        </div>
        
        {/* Worker Pay */}
        <div className="bg-[var(--background-subtle)] rounded-lg p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-[var(--primary)]" />
              <span className="text-[var(--text-muted)]">Worker Pay</span>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-[var(--primary)]">
                ${workerPay.toFixed(2)}
              </p>
              {urgentBonus > 0 && (
                <p className="text-sm text-green-500">
                  +${urgentBonus.toFixed(2)} urgent bonus
                </p>
              )}
            </div>
          </div>
        </div>
        
        {/* Special Instructions Preview */}
        {job.special_instructions && (
          <div className="text-sm text-[var(--text-muted)] italic line-clamp-2">
            "{job.special_instructions}"
          </div>
        )}
      </div>
      
      {/* Action Buttons */}
      <div className="px-4 pb-4 flex gap-3">
        <button
          onClick={() => onSelect?.(job)}
          className="flex-1 py-3 px-4 border border-[var(--border-primary)] rounded-xl text-[var(--text-primary)] font-medium hover:bg-[var(--background-subtle)] transition-colors"
          data-testid={`view-job-${job.id}`}
        >
          View Details
        </button>
        <button
          onClick={() => onPickup?.(job)}
          disabled={!job.can_claim && !job.already_claimed_by_me}
          className={`flex-1 py-3 px-4 rounded-xl font-bold transition-colors ${
            job.already_claimed_by_me
              ? 'bg-green-500/20 text-green-500 border border-green-500'
              : job.can_claim
                ? 'bg-[var(--primary)] text-black hover:bg-[var(--primary-hover)]'
                : 'bg-gray-500/20 text-gray-500 cursor-not-allowed'
          }`}
          data-testid={`pickup-job-${job.id}`}
        >
          {job.already_claimed_by_me ? 'Claimed' : 'PICK UP JOB'}
        </button>
      </div>
    </motion.div>
  );
};

export default WorkerJobCard;
